// src/pages/MyApplications.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { applicationApi } from '../../feddback-services/api';
import './MyApplications.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.jmkinfosoft.com';

const MyApplications = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [updatingDocs, setUpdatingDocs] = useState({});
  
  // ✅ Payment Popup State
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [loadingPayment, setLoadingPayment] = useState(false);

  // ✅ Job Application Form State
  const [showFormPopup, setShowFormPopup] = useState(false);
  const [formData, setFormData] = useState(null);
  const [loadingForm, setLoadingForm] = useState(false);
  const [updatingForm, setUpdatingForm] = useState(false);
  const [selectedFormApplication, setSelectedFormApplication] = useState(null);

  // ✅ Form fields
  const [formFields, setFormFields] = useState({
    full_name: '',
    father_name: '',
    mobile: '',
    emergency_contact: '',
    email: '',
    aadhar_number: '',
    pan_number: '',
    dob: '',
    place_of_birth: '',
    current_address: { full_address: '', tehsil: '', district: '', state: '', pin: '' },
    permanent_address: { full_address: '', tehsil: '', district: '', state: '', pin: '' },
    permanent_same_as_current: false,
    location_applying_for: '',
    identification_mark: '',
    height: '',
    eye_color: '',
    hair_color: '',
    education: [
      { qualification: 'H.S.C (XIIth)', course_stream: '', board_university: '', percentage: '', year_of_passing: '' },
      { qualification: 'Graduation', course_stream: '', board_university: '', percentage: '', year_of_passing: '' },
      { qualification: 'Post-Graduation', course_stream: '', board_university: '', percentage: '', year_of_passing: '' },
      { qualification: 'Computer Literacy', course_stream: '', board_university: '', percentage: '', year_of_passing: '' }
    ],
    previous_experience: [{ employer_name: '', designation: '', joining_date: '', leaving_date: '', monthly_salary: '', pf_uan_esic_no: '' }],
    bank_details: { bank_name: '', account_holder_name: '', account_number: '', ifsc_code: '', branch_name: '' }
  });

  const [photoFile, setPhotoFile] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('userRole');
    
    if (!token || role !== 'candidate') {
      navigate('/login');
      return;
    }
    
    fetchApplications();
  }, [pagination.page]);

  const fetchApplications = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await applicationApi.list({
        page: pagination.page,
        limit: pagination.limit,
      });
      
      const data = response.data.data || response.data;
      const items = data.items || [];
      
      // ✅ Fetch payment details and form status for JOINED applications
      for (let app of items) {
        if (app.status === 'joined') {
          try {
            const detailResponse = await applicationApi.get(app.id);
            const detailData = detailResponse.data.data || detailResponse.data;
            app.payment_details = detailData.salary_payments || detailData.training_stipends || [];
            app.training_join_date = detailData.training_join_date || null;
            app.training_end_date = detailData.training_end_date || null;
            
            // ✅ Get form completion status
            try {
              const formResponse = await fetch(`${API_BASE_URL}/api/applications/${app.id}/joined-form`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
              });
              if (formResponse.ok) {
                const formResult = await formResponse.json();
                const formData = formResult.data || formResult;
                app.form_completion_status = formData.completion_status || 'not_started';
                app.form_has_photo = formData.has_photo || false;
                app.form_has_signature = formData.has_signature || false;
              }
            } catch (formErr) {
              console.error('Failed to fetch form status:', formErr);
              app.form_completion_status = 'not_started';
            }
          } catch (err) {
            console.error(`Failed to fetch details for ${app.id}:`, err);
            app.payment_details = [];
          }
        }
      }
      
      setApplications(items);
      setPagination(data.pagination || pagination);
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError('Failed to load applications. Please try again.');
      if (err.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'applied': { class: 'status-applied', label: 'Applied', icon: 'fa-clock' },
      'shortlisted': { class: 'status-shortlisted', label: 'Shortlisted', icon: 'fa-star' },
      'interview': { class: 'status-interview', label: 'Interview', icon: 'fa-handshake' },
      'offered': { class: 'status-offered', label: 'Offered', icon: 'fa-gift' },
      'training': { class: 'status-training', label: 'Training', icon: 'fa-graduation-cap' },
      'joined': { class: 'status-joined', label: 'Joined', icon: 'fa-check-circle' },
      'rejected': { class: 'status-rejected', label: 'Rejected', icon: 'fa-times-circle' },
      'withdrawn': { class: 'status-withdrawn', label: 'Withdrawn', icon: 'fa-undo' },
    };
    const s = statusMap[status?.toLowerCase()] || statusMap['applied'];
    return (
      <span className={`status-badge ${s.class}`}>
        <i className={`fas ${s.icon}`}></i>
        {s.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatMonthDisplay = (monthStr) => {
    if (!monthStr) return 'N/A';
    const [year, month] = monthStr.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const handleBack = () => {
    navigate('/user/view-requirement');
  };

  const handleDocumentsSubmittedChange = async (applicationId, value) => {
    if (updatingDocs[applicationId]) return;
    
    setUpdatingDocs(prev => ({ ...prev, [applicationId]: true }));
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/api/applications/${applicationId}/documents`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          documents_status: value ? 'submitted' : 'not_submitted'
        })
      });
      
      const result = await response.json();
      
      if (response.ok && (result.success || result.status === 'success')) {
        setApplications(prev => prev.map(app => 
          app.id === applicationId 
            ? { ...app, documents_status: value ? 'submitted' : 'not_submitted' } 
            : app
        ));
        alert(`✅ Documents submission status updated to: ${value ? 'Submitted' : 'Not Submitted'}`);
      } else {
        alert('❌ Failed to update: ' + (result.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error updating documents status:', err);
      alert('❌ Failed to update. Please try again.');
    } finally {
      setUpdatingDocs(prev => ({ ...prev, [applicationId]: false }));
    }
  };

  const handleViewPayment = async (app) => {
    setSelectedApplication(app);
    setLoadingPayment(true);
    setShowPaymentPopup(true);
    
    try {
      const response = await applicationApi.get(app.id);
      const detailData = response.data.data || response.data;
      const payments = detailData.salary_payments || detailData.training_stipends || [];
      
      const formattedPayments = payments.map(p => ({
        ...p,
        stipend_month_display: formatMonthDisplay(p.stipend_month),
        government_date_display: p.government_date ? formatDateOnly(p.government_date) : 'N/A',
        candidate_date_display: p.candidate_date ? formatDateOnly(p.candidate_date) : 'N/A',
        amount_display: p.amount ? `₹${p.amount.toLocaleString()}` : 'N/A'
      }));
      
      setPaymentDetails(formattedPayments);
    } catch (err) {
      console.error('Error fetching payment details:', err);
      setPaymentDetails(app.payment_details || []);
    } finally {
      setLoadingPayment(false);
    }
  };

  const handleClosePaymentPopup = () => {
    setShowPaymentPopup(false);
    setSelectedApplication(null);
    setPaymentDetails([]);
  };

  const getPaymentSummary = (payments) => {
    if (!payments || payments.length === 0) {
      return { total: 0, paid: 0, pending: 0, label: 'No payments' };
    }
    
    const total = payments.length;
    const paid = payments.filter(p => p.candidate_status === 'paid').length;
    const pending = payments.filter(p => p.candidate_status === 'not_paid' || !p.candidate_status).length;
    
    let label = `${paid}/${total} paid`;
    if (paid === total && total > 0) label = '✅ All paid';
    else if (paid === 0) label = '⏳ No payments';
    
    return { total, paid, pending, label };
  };

  const getFormStatusDisplay = (status) => {
    const statusMap = {
      'not_started': { label: '⏳ Not Started', class: 'form-status-not-started' },
      'in_progress': { label: '⏳ In Progress', class: 'form-status-in-progress' },
      'completed': { label: '✅ Completed', class: 'form-status-completed' }
    };
    return statusMap[status] || statusMap['not_started'];
  };

  // ✅ Open Form - Only when first opened
  const openForm = async (app) => {
    setSelectedFormApplication(app);
    setLoadingForm(true);
    setShowFormPopup(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/applications/${app.id}/joined-form`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch form data');
      }
      
      const result = await response.json();
      const data = result.data || result;
      
      if (data.form_data) {
        setFormFields(data.form_data);
      }
      
      setFormData(data);
      
      if (data.photo_url) {
        setPhotoPreview(data.photo_url);
      }
      if (data.signature_url) {
        setSignaturePreview(data.signature_url);
      }
      
    } catch (err) {
      console.error('Error fetching form:', err);
      alert('Failed to load form data. Please try again.');
    } finally {
      setLoadingForm(false);
    }
  };

  const handleFormFieldChange = (field, value) => {
    setFormFields(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedFieldChange = (parent, field, value) => {
    setFormFields(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const handleEducationChange = (index, field, value) => {
    setFormFields(prev => {
      const newEducation = [...prev.education];
      newEducation[index] = { ...newEducation[index], [field]: value };
      return { ...prev, education: newEducation };
    });
  };

  const handleExperienceChange = (index, field, value) => {
    setFormFields(prev => {
      const newExperience = [...prev.previous_experience];
      newExperience[index] = { ...newExperience[index], [field]: value };
      return { ...prev, previous_experience: newExperience };
    });
  };

  const addExperienceRow = () => {
    setFormFields(prev => ({
      ...prev,
      previous_experience: [
        ...prev.previous_experience,
        { employer_name: '', designation: '', joining_date: '', leaving_date: '', monthly_salary: '', pf_uan_esic_no: '' }
      ]
    }));
  };

  const removeExperienceRow = (index) => {
    setFormFields(prev => ({
      ...prev,
      previous_experience: prev.previous_experience.filter((_, i) => i !== index)
    }));
  };

  // ✅ Handle File Upload - Without Page Refresh
  const handleFileUpload = async (type, file) => {
    if (!file) return;
    
    setUpdatingForm(true);
    
    const formDataFile = new FormData();
    formDataFile.append('file', file);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/applications/${selectedFormApplication.id}/joined-form/${type}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formDataFile
      });
      
      if (!response.ok) {
        throw new Error(`Failed to upload ${type}`);
      }
      
      const result = await response.json();
      const data = result.data || result;
      
      // ✅ Update preview without page refresh
      if (type === 'photo') {
        setPhotoPreview(URL.createObjectURL(file));
        setPhotoFile(file);
      } else {
        setSignaturePreview(URL.createObjectURL(file));
        setSignatureFile(file);
      }
      
      // ✅ Update form data without refreshing
      if (data.form_data) {
        setFormFields(data.form_data);
      }
      
      // ✅ Update form status
      if (data.completion_status) {
        setFormData(prev => ({
          ...prev,
          completion_status: data.completion_status,
          photo_url: type === 'photo' ? URL.createObjectURL(file) : prev?.photo_url,
          signature_url: type === 'signature' ? URL.createObjectURL(file) : prev?.signature_url
        }));
      }
      
      alert(`✅ ${type.charAt(0).toUpperCase() + type.slice(1)} uploaded successfully!`);
      
      // ✅ Refresh applications list in background only
      await fetchApplications();
      
    } catch (err) {
      console.error(`Error uploading ${type}:`, err);
      alert(`Failed to upload ${type}. Please try again.`);
    } finally {
      setUpdatingForm(false);
    }
  };

  // ✅ Submit Form - Without Page Refresh
  const submitForm = async () => {
    if (!selectedFormApplication) return;
    
    setUpdatingForm(true);
    
    try {
      const payload = {
        ...formFields,
        permanent_same_as_current: formFields.permanent_same_as_current || false
      };
      
      if (payload.permanent_same_as_current) {
        payload.permanent_address = { ...payload.current_address };
      }
      
      const response = await fetch(`${API_BASE_URL}/api/applications/${selectedFormApplication.id}/joined-form`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save form');
      }
      
      const result = await response.json();
      const data = result.data || result;
      
      // ✅ Update form data without refresh
      if (data.form_data) {
        setFormFields(data.form_data);
      }
      if (data.completion_status) {
        setFormData(prev => ({
          ...prev,
          completion_status: data.completion_status
        }));
      }
      
      alert(`✅ Form saved successfully! Status: ${data.completion_status || 'Updated'}`);
      
      // ✅ Refresh applications list in background only
      await fetchApplications();
      
    } catch (err) {
      console.error('Error submitting form:', err);
      alert(`❌ Failed to save form: ${err.message}`);
    } finally {
      setUpdatingForm(false);
    }
  };

  if (loading) {
    return (
      <div className="my-applications-loading">
        <div className="spinner"></div>
        <p>Loading your applications...</p>
      </div>
    );
  }

  return (
    <div className="my-applications-container">
      <div className="my-applications-card">
        {/* Header */}
        <div className="my-applications-header">
          <button className="back-btn" onClick={handleBack}>
            <i className="fas fa-arrow-left"></i> Back
          </button>
          <h1>
            <i className="fas fa-file-alt"></i> My Applications
          </h1>
          <span className="total-count">{pagination.total} applications</span>
        </div>

        {error && (
          <div className="error-message">
            <i className="fas fa-exclamation-circle"></i>
            <span>{error}</span>
          </div>
        )}

        {applications.length === 0 ? (
          <div className="empty-applications">
            <i className="fas fa-file-alt"></i>
            <h3>No applications found</h3>
            <p>You haven't applied to any positions yet.</p>
            <button className="browse-jobs-btn" onClick={() => navigate('/user/view-requirement')}>
              <i className="fas fa-search"></i> Browse Jobs
            </button>
          </div>
        ) : (
          <>
            <div className="applications-list">
              {applications.map((app) => {
                const paymentSummary = app.status === 'joined' ? getPaymentSummary(app.payment_details || []) : null;
                const formStatus = app.status === 'joined' ? getFormStatusDisplay(app.form_completion_status || 'not_started') : null;
                
                return (
                  <div key={app.id} className="application-card">
                    <div className="application-header">
                      <div className="application-title">
                        <h3>{app.requirement_title || 'Unknown Position'}</h3>
                        <span className="application-code">{app.requirement_code || 'N/A'}</span>
                      </div>
                      {getStatusBadge(app.status)}
                    </div>

                    <div className="application-details">
                      <div className="detail-item">
                        <i className="fas fa-building"></i>
                        <span>{app.department || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <i className="fas fa-map-marker-alt"></i>
                        <span>{app.location || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <i className="fas fa-calendar"></i>
                        <span>Applied: {formatDate(app.applied_at)}</span>
                      </div>
                      <div className="detail-item">
                        <i className="fas fa-code-branch"></i>
                        <span>Source: {app.source || 'Website'}</span>
                      </div>
                      
                      {app.status === 'training' && (
                        <div className="detail-item documents-status">
                          <i className="fas fa-file-pdf"></i>
                          <span>Documents: {app.documents_status === 'submitted' ? '✅ Submitted' : app.documents_status === 'partially_submitted' ? '⏳ Partially Submitted' : '⏳ Pending'}</span>
                        </div>
                      )}
                      
                      {app.status === 'training' && (app.training_join_date || app.training_end_date) && (
                        <>
                          {app.training_join_date && (
                            <div className="detail-item">
                              <i className="fas fa-calendar-plus"></i>
                              <span>Joining: {formatDateOnly(app.training_join_date)}</span>
                            </div>
                          )}
                          {app.training_end_date && (
                            <div className="detail-item">
                              <i className="fas fa-calendar-minus"></i>
                              <span>Ending: {formatDateOnly(app.training_end_date)}</span>
                            </div>
                          )}
                        </>
                      )}
                      
                      {app.status === 'joined' && paymentSummary && (
                        <div className="detail-item payment-summary">
                          <i className="fas fa-money-bill-wave" style={{ color: '#27ae60' }}></i>
                          <span>Payment: {paymentSummary.label}</span>
                          <button 
                            className="btn-view-payment"
                            onClick={() => handleViewPayment(app)}
                            title="View Payment Details"
                          >
                            <i className="fas fa-eye"></i> View
                          </button>
                        </div>
                      )}
                      
                      {app.status === 'joined' && formStatus && (
                        <div className="detail-item form-status">
                          <i className="fas fa-file-signature"></i>
                          <span>Application Form: <span className={formStatus.class}>{formStatus.label}</span></span>
                          {app.form_completion_status !== 'completed' && (
                            <button 
                              className="btn-fill-form"
                              onClick={() => openForm(app)}
                              title="Fill Job Application Form"
                            >
                              <i className="fas fa-pen"></i> Fill Form
                            </button>
                          )}
                          {app.form_completion_status === 'completed' && (
                            <button 
                              className="btn-view-form"
                              onClick={() => openForm(app)}
                              title="View Job Application Form"
                            >
                              <i className="fas fa-eye"></i> View Form
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {app.status === 'training' && (
                      <div className="documents-section-card">
                        <div className="documents-label-card">
                          <i className="fas fa-file-pdf"></i>
                          <span>Documents Submitted?</span>
                        </div>
                        <div className="radio-options-card">
                          <label className={`radio-option-card ${app.documents_status === 'submitted' ? 'selected-yes' : ''}`}>
                            <input
                              type="radio"
                              name={`documents_${app.id}`}
                              value="yes"
                              checked={app.documents_status === 'submitted'}
                              onChange={() => handleDocumentsSubmittedChange(app.id, true)}
                              disabled={updatingDocs[app.id]}
                            />
                            <span className="radio-custom-card yes">
                              <i className="fas fa-check-circle"></i> Yes
                            </span>
                          </label>
                          <label className={`radio-option-card ${app.documents_status === 'not_submitted' ? 'selected-no' : ''}`}>
                            <input
                              type="radio"
                              name={`documents_${app.id}`}
                              value="no"
                              checked={app.documents_status === 'not_submitted'}
                              onChange={() => handleDocumentsSubmittedChange(app.id, false)}
                              disabled={updatingDocs[app.id]}
                            />
                            <span className="radio-custom-card no">
                              <i className="fas fa-times-circle"></i> No
                            </span>
                          </label>
                          {updatingDocs[app.id] && (
                            <span className="updating-spinner">
                              <i className="fas fa-spinner fa-spin"></i>
                            </span>
                          )}
                        </div>
                        {app.documents_status && !updatingDocs[app.id] && (
                          <div className={`submission-status-card ${app.documents_status === 'submitted' ? 'submitted' : 'pending'}`}>
                            <i className={`fas ${app.documents_status === 'submitted' ? 'fa-check-circle' : 'fa-clock'}`}></i>
                            {app.documents_status === 'submitted' ? '✅ Submitted' : app.documents_status === 'partially_submitted' ? '⏳ Partially Submitted' : '⏳ Pending'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {pagination.totalPages > 1 && (
              <div className="pagination">
                <button 
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="page-btn"
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                <span className="page-info">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button 
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="page-btn"
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ✅ Payment Details Popup */}
      {showPaymentPopup && (
        <div className="payment-popup-overlay" onClick={handleClosePaymentPopup}>
          <div className="payment-popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="payment-popup-header">
              <div className="payment-popup-title">
                <h2><i className="fas fa-money-bill-wave" style={{ color: '#27ae60' }}></i> Payment Details</h2>
                <p className="payment-popup-subtitle">
                  {selectedApplication?.candidate_name || 'Candidate'} - {selectedApplication?.requirement_title || 'Position'}
                </p>
              </div>
              <button 
                className="payment-popup-close" 
                onClick={handleClosePaymentPopup}
                title="Close"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="payment-popup-body">
              {loadingPayment ? (
                <div className="payment-loading">
                  <i className="fas fa-spinner fa-spin"></i>
                  <p>Loading payment details...</p>
                </div>
              ) : (
                <>
                  {paymentDetails.filter(p => p.candidate_status === 'paid' || p.candidate_status === 'not_paid').length === 0 ? (
                    <div className="payment-empty">
                      <i className="fas fa-receipt"></i>
                      <p>No payment records found</p>
                      <span>Payments will appear here once processed</span>
                    </div>
                  ) : (
                    <>
                      <div className="payment-summary-cards">
                        <div className="summary-card">
                          <span className="summary-label">Total Months</span>
                          <span className="summary-value">{paymentDetails.length}</span>
                        </div>
                        <div className="summary-card paid">
                          <span className="summary-label">Paid</span>
                          <span className="summary-value">{paymentDetails.filter(p => p.candidate_status === 'paid').length}</span>
                        </div>
                        <div className="summary-card pending">
                          <span className="summary-label">Pending</span>
                          <span className="summary-value">{paymentDetails.filter(p => p.candidate_status === 'not_paid' || !p.candidate_status).length}</span>
                        </div>
                      </div>
                      
                      <div className="payment-list-container">
                        <h4 className="payment-list-title">Payment Status</h4>
                        <div className="payment-list">
                          {paymentDetails
                            .filter(p => p.candidate_status === 'paid' || p.candidate_status === 'not_paid')
                            .map((payment, idx) => {
                              const isPaid = payment.candidate_status === 'paid';
                              
                              let statusDisplay = '';
                              let statusClass = '';
                              if (isPaid) {
                                const paidDate = payment.candidate_date ? formatDateOnly(payment.candidate_date) : 'N/A';
                                statusDisplay = `✅ Paid on ${paidDate}`;
                                statusClass = 'paid';
                              } else {
                                const reason = payment.not_paid_reason || payment.remark || 'No reason provided';
                                statusDisplay = `❌ Not Paid - ${reason}`;
                                statusClass = 'not-paid';
                              }
                              
                              return (
                                <div key={idx} className={`payment-status-item ${statusClass}`}>
                                  <span className="payment-status-text">{statusDisplay}</span>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ✅ Job Application Form Popup - Photo & Signature at Top */}
      {showFormPopup && selectedFormApplication && (
        <div className="form-popup-overlay" onClick={() => setShowFormPopup(false)}>
          <div className="form-popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="form-popup-header">
              <div className="form-popup-title">
                <h2><i className="fas fa-file-signature" style={{ color: '#6c63ff' }}></i> Job Application Form</h2>
                <p className="form-popup-subtitle">
                  {selectedFormApplication?.candidate_name || 'Candidate'} - {selectedFormApplication?.requirement_title || 'Position'}
                </p>
                {formData && (
                  <span className={`form-status-badge ${formData.completion_status === 'completed' ? 'status-completed' : formData.completion_status === 'in_progress' ? 'status-in-progress' : 'status-not-started'}`}>
                    {formData.completion_status === 'completed' ? '✅ Completed' : 
                     formData.completion_status === 'in_progress' ? '⏳ In Progress' : '⏳ Not Started'}
                  </span>
                )}
              </div>
              <button className="form-popup-close" onClick={() => setShowFormPopup(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="form-popup-body">
              {loadingForm ? (
                <div className="form-loading">
                  <i className="fas fa-spinner fa-spin"></i>
                  <p>Loading form...</p>
                </div>
              ) : (
                <form className="job-application-form" onSubmit={(e) => { e.preventDefault(); submitForm(); }}>
                  
                  {/* ✅ Photo & Signature Upload - येथे सुरुवातीला */}
                  <div className="form-section photo-signature-section">
                    <h3 className="form-section-title"><i className="fas fa-image" style={{ color: '#6c63ff' }}></i> Photo & Signature</h3>
                    <p className="form-section-desc">Please upload your recent passport size photo and signature</p>
                    <div className="form-row file-upload-row">
                      <div className="form-group file-upload-group">
                        <label>Photo <span className="required">*</span></label>
                        <div className="file-upload-wrapper">
                          <input type="file" accept="image/*" onChange={(e) => {
                            if (e.target.files[0]) {
                              handleFileUpload('photo', e.target.files[0]);
                            }
                          }} />
                          <span className="file-upload-label">
                            <i className="fas fa-cloud-upload-alt"></i> Upload Photo
                          </span>
                        </div>
                        {photoPreview && (
                          <div className="file-preview">
                            <img src={photoPreview} alt="Photo" />
                            <span className="file-preview-label">Photo Uploaded</span>
                          </div>
                        )}
                      </div>
                      <div className="form-group file-upload-group">
                        <label>Signature <span className="required">*</span></label>
                        <div className="file-upload-wrapper">
                          <input type="file" accept="image/*" onChange={(e) => {
                            if (e.target.files[0]) {
                              handleFileUpload('signature', e.target.files[0]);
                            }
                          }} />
                          <span className="file-upload-label">
                            <i className="fas fa-cloud-upload-alt"></i> Upload Signature
                          </span>
                        </div>
                        {signaturePreview && (
                          <div className="file-preview">
                            <img src={signaturePreview} alt="Signature" />
                            <span className="file-preview-label">Signature Uploaded</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Personal Information */}
                  <div className="form-section">
                    <h3 className="form-section-title"><i className="fas fa-user"></i> Personal Information</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Full Name <span className="required">*</span></label>
                        <input type="text" className="form-input" value={formFields.full_name || ''} 
                          onChange={(e) => handleFormFieldChange('full_name', e.target.value)} required />
                      </div>
                      <div className="form-group">
                        <label>Father's Name <span className="required">*</span></label>
                        <input type="text" className="form-input" value={formFields.father_name || ''} 
                          onChange={(e) => handleFormFieldChange('father_name', e.target.value)} required />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Mobile Number <span className="required">*</span></label>
                        <input type="tel" className="form-input" value={formFields.mobile || ''} 
                          onChange={(e) => handleFormFieldChange('mobile', e.target.value)} required />
                      </div>
                      <div className="form-group">
                        <label>Emergency Contact <span className="required">*</span></label>
                        <input type="tel" className="form-input" value={formFields.emergency_contact || ''} 
                          onChange={(e) => handleFormFieldChange('emergency_contact', e.target.value)} required />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Email <span className="required">*</span></label>
                        <input type="email" className="form-input" value={formFields.email || ''} 
                          onChange={(e) => handleFormFieldChange('email', e.target.value)} required />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Aadhar Number <span className="required">*</span></label>
                        <input type="text" className="form-input" value={formFields.aadhar_number || ''} 
                          onChange={(e) => handleFormFieldChange('aadhar_number', e.target.value)} required />
                      </div>
                      <div className="form-group">
                        <label>PAN Number <span className="required">*</span></label>
                        <input type="text" className="form-input" value={formFields.pan_number || ''} 
                          onChange={(e) => handleFormFieldChange('pan_number', e.target.value)} required />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Date of Birth <span className="required">*</span></label>
                        <input type="date" className="form-input" value={formFields.dob || ''} 
                          onChange={(e) => handleFormFieldChange('dob', e.target.value)} required />
                      </div>
                      <div className="form-group">
                        <label>Place of Birth <span className="required">*</span></label>
                        <input type="text" className="form-input" value={formFields.place_of_birth || ''} 
                          onChange={(e) => handleFormFieldChange('place_of_birth', e.target.value)} required />
                      </div>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="form-section">
                    <h3 className="form-section-title"><i className="fas fa-map-marker-alt"></i> Address</h3>
                    <h4 className="form-sub-title">Current Address <span className="required">*</span></h4>
                    <div className="form-row">
                      <div className="form-group full-width">
                        <label>Full Address</label>
                        <input type="text" className="form-input" value={formFields.current_address?.full_address || ''} 
                          onChange={(e) => handleNestedFieldChange('current_address', 'full_address', e.target.value)} required />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Tehsil</label>
                        <input type="text" className="form-input" value={formFields.current_address?.tehsil || ''} 
                          onChange={(e) => handleNestedFieldChange('current_address', 'tehsil', e.target.value)} required />
                      </div>
                      <div className="form-group">
                        <label>District</label>
                        <input type="text" className="form-input" value={formFields.current_address?.district || ''} 
                          onChange={(e) => handleNestedFieldChange('current_address', 'district', e.target.value)} required />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>State</label>
                        <input type="text" className="form-input" value={formFields.current_address?.state || ''} 
                          onChange={(e) => handleNestedFieldChange('current_address', 'state', e.target.value)} required />
                      </div>
                      <div className="form-group">
                        <label>PIN Code</label>
                        <input type="text" className="form-input" value={formFields.current_address?.pin || ''} 
                          onChange={(e) => handleNestedFieldChange('current_address', 'pin', e.target.value)} required />
                      </div>
                    </div>

                    <div className="form-checkbox">
                      <input type="checkbox" id="permanent_same" checked={formFields.permanent_same_as_current || false}
                        onChange={(e) => handleFormFieldChange('permanent_same_as_current', e.target.checked)} />
                      <label htmlFor="permanent_same">Permanent address same as current</label>
                    </div>

                    {!formFields.permanent_same_as_current && (
                      <>
                        <h4 className="form-sub-title">Permanent Address</h4>
                        <div className="form-row">
                          <div className="form-group full-width">
                            <label>Full Address</label>
                            <input type="text" className="form-input" value={formFields.permanent_address?.full_address || ''} 
                              onChange={(e) => handleNestedFieldChange('permanent_address', 'full_address', e.target.value)} />
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Tehsil</label>
                            <input type="text" className="form-input" value={formFields.permanent_address?.tehsil || ''} 
                              onChange={(e) => handleNestedFieldChange('permanent_address', 'tehsil', e.target.value)} />
                          </div>
                          <div className="form-group">
                            <label>District</label>
                            <input type="text" className="form-input" value={formFields.permanent_address?.district || ''} 
                              onChange={(e) => handleNestedFieldChange('permanent_address', 'district', e.target.value)} />
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label>State</label>
                            <input type="text" className="form-input" value={formFields.permanent_address?.state || ''} 
                              onChange={(e) => handleNestedFieldChange('permanent_address', 'state', e.target.value)} />
                          </div>
                          <div className="form-group">
                            <label>PIN Code</label>
                            <input type="text" className="form-input" value={formFields.permanent_address?.pin || ''} 
                              onChange={(e) => handleNestedFieldChange('permanent_address', 'pin', e.target.value)} />
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Location & Physical Details */}
                  <div className="form-section">
                    <h3 className="form-section-title"><i className="fas fa-info-circle"></i> Location & Physical Details</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Location Applying For <span className="required">*</span></label>
                        <input type="text" className="form-input" value={formFields.location_applying_for || ''} 
                          onChange={(e) => handleFormFieldChange('location_applying_for', e.target.value)} required />
                      </div>
                      <div className="form-group">
                        <label>Identification Mark <span className="required">*</span></label>
                        <input type="text" className="form-input" value={formFields.identification_mark || ''} 
                          onChange={(e) => handleFormFieldChange('identification_mark', e.target.value)} required />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Height <span className="required">*</span></label>
                        <input type="text" className="form-input" placeholder="e.g. 5ft 8in" value={formFields.height || ''} 
                          onChange={(e) => handleFormFieldChange('height', e.target.value)} required />
                      </div>
                      <div className="form-group">
                        <label>Eye Color <span className="required">*</span></label>
                        <input type="text" className="form-input" value={formFields.eye_color || ''} 
                          onChange={(e) => handleFormFieldChange('eye_color', e.target.value)} required />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Hair Color <span className="required">*</span></label>
                        <input type="text" className="form-input" value={formFields.hair_color || ''} 
                          onChange={(e) => handleFormFieldChange('hair_color', e.target.value)} required />
                      </div>
                    </div>
                  </div>

                  {/* Education */}
                  <div className="form-section">
                    <h3 className="form-section-title"><i className="fas fa-graduation-cap"></i> Education</h3>
                    {formFields.education && formFields.education.map((edu, index) => (
                      <div key={index} className="education-row">
                        <h4 className="form-sub-title">{edu.qualification}</h4>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Course/Stream</label>
                            <input type="text" className="form-input" value={edu.course_stream || ''} 
                              onChange={(e) => handleEducationChange(index, 'course_stream', e.target.value)} />
                          </div>
                          <div className="form-group">
                            <label>Board/University</label>
                            <input type="text" className="form-input" value={edu.board_university || ''} 
                              onChange={(e) => handleEducationChange(index, 'board_university', e.target.value)} />
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Percentage</label>
                            <input type="text" className="form-input" value={edu.percentage || ''} 
                              onChange={(e) => handleEducationChange(index, 'percentage', e.target.value)} />
                          </div>
                          <div className="form-group">
                            <label>Year of Passing</label>
                            <input type="text" className="form-input" value={edu.year_of_passing || ''} 
                              onChange={(e) => handleEducationChange(index, 'year_of_passing', e.target.value)} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Previous Experience */}
                  <div className="form-section">
                    <h3 className="form-section-title"><i className="fas fa-briefcase"></i> Previous Experience</h3>
                    {formFields.previous_experience && formFields.previous_experience.map((exp, index) => (
                      <div key={index} className="experience-row">
                        <div className="experience-header">
                          <h4 className="form-sub-title">Experience #{index + 1}</h4>
                          {index > 0 && (
                            <button type="button" className="btn-remove-experience" onClick={() => removeExperienceRow(index)}>
                              <i className="fas fa-times"></i> Remove
                            </button>
                          )}
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Employer Name</label>
                            <input type="text" className="form-input" value={exp.employer_name || ''} 
                              onChange={(e) => handleExperienceChange(index, 'employer_name', e.target.value)} />
                          </div>
                          <div className="form-group">
                            <label>Designation</label>
                            <input type="text" className="form-input" value={exp.designation || ''} 
                              onChange={(e) => handleExperienceChange(index, 'designation', e.target.value)} />
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Joining Date</label>
                            <input type="date" className="form-input" value={exp.joining_date || ''} 
                              onChange={(e) => handleExperienceChange(index, 'joining_date', e.target.value)} />
                          </div>
                          <div className="form-group">
                            <label>Leaving Date</label>
                            <input type="date" className="form-input" value={exp.leaving_date || ''} 
                              onChange={(e) => handleExperienceChange(index, 'leaving_date', e.target.value)} />
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Monthly Salary</label>
                            <input type="text" className="form-input" value={exp.monthly_salary || ''} 
                              onChange={(e) => handleExperienceChange(index, 'monthly_salary', e.target.value)} />
                          </div>
                          <div className="form-group">
                            <label>PF/UAN/ESIC No.</label>
                            <input type="text" className="form-input" value={exp.pf_uan_esic_no || ''} 
                              onChange={(e) => handleExperienceChange(index, 'pf_uan_esic_no', e.target.value)} />
                          </div>
                        </div>
                      </div>
                    ))}
                    <button type="button" className="btn-add-experience" onClick={addExperienceRow}>
                      <i className="fas fa-plus"></i> Add Experience
                    </button>
                  </div>

                  {/* Bank Details */}
                  <div className="form-section">
                    <h3 className="form-section-title"><i className="fas fa-university"></i> Bank Details</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Bank Name <span className="required">*</span></label>
                        <input type="text" className="form-input" value={formFields.bank_details?.bank_name || ''} 
                          onChange={(e) => handleNestedFieldChange('bank_details', 'bank_name', e.target.value)} required />
                      </div>
                      <div className="form-group">
                        <label>Account Holder Name <span className="required">*</span></label>
                        <input type="text" className="form-input" value={formFields.bank_details?.account_holder_name || ''} 
                          onChange={(e) => handleNestedFieldChange('bank_details', 'account_holder_name', e.target.value)} required />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Account Number <span className="required">*</span></label>
                        <input type="text" className="form-input" value={formFields.bank_details?.account_number || ''} 
                          onChange={(e) => handleNestedFieldChange('bank_details', 'account_number', e.target.value)} required />
                      </div>
                      <div className="form-group">
                        <label>IFSC Code <span className="required">*</span></label>
                        <input type="text" className="form-input" value={formFields.bank_details?.ifsc_code || ''} 
                          onChange={(e) => handleNestedFieldChange('bank_details', 'ifsc_code', e.target.value)} required />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Branch Name</label>
                        <input type="text" className="form-input" value={formFields.bank_details?.branch_name || ''} 
                          onChange={(e) => handleNestedFieldChange('bank_details', 'branch_name', e.target.value)} />
                      </div>
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="form-actions">
                    <button type="button" className="btn-close-form" onClick={() => setShowFormPopup(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-submit-form" disabled={updatingForm}>
                      {updatingForm ? (
                        <><i className="fas fa-spinner fa-spin"></i> Saving...</>
                      ) : (
                        <><i className="fas fa-save"></i> Save Form</>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyApplications;